import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  Chart as ChartJS,
  ArcElement, CategoryScale, LinearScale,
  BarElement, PointElement, LineElement,
  Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Doughnut, Bar, Line } from 'react-chartjs-2';
import { adminApi } from '../../../api/adminApi';

// Hook: detecta si la pantalla es chica (mobile/tablet)
function useEsMobile(breakpoint = 600) {
  const [esMobile, setEsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const handler = () => setEsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return esMobile;
}

ChartJS.register(
  ArcElement, CategoryScale, LinearScale,
  BarElement, PointElement, LineElement,
  Title, Tooltip, Legend, Filler,
);

// Paleta consistente
const COLORS = [
  '#1976d2', '#43a047', '#fb8c00', '#e53935', '#8e24aa',
  '#00acc1', '#f4511e', '#5e35b1', '#3949ab', '#00897b',
  '#7cb342', '#c0ca33', '#fdd835', '#6d4c41', '#546e7a',
];
const COLOR_SIN_CASINO = '#9e9e9e';
const COLOR_ENCONTRADO = '#e53935';
const COLOR_NO_ENCONTRADO = '#43a047';

const TIPO_LABELS = {
  'buscar-dni': 'DNI',
  'buscar-nombre': 'Nombre',
  'escanear-qr': 'QR',
  'ver-detalle': 'Ver detalle',
};

const PRESETS_FECHA = [
  { label: 'Últimos 7 días', value: 7 },
  { label: 'Últimos 30 días', value: 30 },
  { label: 'Últimos 90 días', value: 90 },
];

function toIsoDate(d) {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function fmtPct(n) {
  if (n === null || n === undefined || isNaN(n)) return '—';
  return (n * 100).toFixed(1) + '%';
}

function fmtNum(n) {
  if (n === null || n === undefined || isNaN(n)) return '—';
  return n.toLocaleString('es-AR');
}

export default function PanelEstadisticasConsultas() {
  const toast = useRef(null);
  const esMobile = useEsMobile();
  const [fechaDesde, setFechaDesde] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  });
  const [fechaHasta, setFechaHasta] = useState(new Date());
  const [casinoId, setCasinoId] = useState(null);
  const [casinos, setCasinos] = useState([]);
  const [stats, setStats] = useState(null);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    cargarCasinos();
    cargarStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function cargarCasinos() {
    try {
      const res = await adminApi.getCasinos();
      setCasinos([
        { label: 'Todos los casinos', value: null },
        ...res.data.map((c) => ({ label: c.nombre, value: c.id })),
      ]);
    } catch (e) {
      // silencio: no es bloqueante
    }
  }

  async function cargarStats() {
    setCargando(true);
    try {
      const params = {
        fechaDesde: toIsoDate(fechaDesde),
        fechaHasta: toIsoDate(fechaHasta),
      };
      if (casinoId) params.casinoId = casinoId;
      const res = await adminApi.getStatsConsultasAutoexcluidos(params);
      setStats(res.data);
    } catch (e) {
      const body = e?.response?.data;
      const isBuilding = e?.response?.status === 503;
      toast.current?.show({
        severity: isBuilding ? 'warn' : 'error',
        summary: isBuilding ? 'Índice en construcción' : 'Error',
        detail: body?.message || 'No se pudieron cargar las estadísticas',
        life: 8000,
      });
    } finally {
      setCargando(false);
    }
  }

  function aplicarPreset(dias) {
    const hasta = new Date();
    const desde = new Date();
    desde.setDate(desde.getDate() - dias);
    setFechaDesde(desde);
    setFechaHasta(hasta);
  }

  // ─── Datasets para gráficos (memoizados) ───────────────────────────────────
  const dsCasinos = useMemo(() => {
    if (!stats) return null;
    return {
      labels: stats.porCasino.map((c) => c.nombre),
      datasets: [{
        data: stats.porCasino.map((c) => c.count),
        backgroundColor: stats.porCasino.map((c, i) =>
          c.id === null ? COLOR_SIN_CASINO : COLORS[i % COLORS.length]
        ),
      }],
    };
  }, [stats]);

  const dsFecha = useMemo(() => {
    if (!stats) return null;
    return {
      labels: stats.porFecha.map((f) => f.fecha),
      datasets: [{
        label: 'Consultas',
        data: stats.porFecha.map((f) => f.count),
        borderColor: COLORS[0],
        backgroundColor: COLORS[0] + '33',
        fill: true,
        tension: 0.25,
      }],
    };
  }, [stats]);

  const dsHora = useMemo(() => {
    if (!stats) return null;
    return {
      labels: stats.porHora.map((h) => `${String(h.hora).padStart(2, '0')}h`),
      datasets: [{
        label: 'Consultas',
        data: stats.porHora.map((h) => h.count),
        backgroundColor: COLORS[2],
      }],
    };
  }, [stats]);

  const dsDiaSemana = useMemo(() => {
    if (!stats) return null;
    return {
      labels: stats.porDiaSemana.map((d) => d.label),
      datasets: [{
        label: 'Consultas',
        data: stats.porDiaSemana.map((d) => d.count),
        backgroundColor: COLORS[4],
      }],
    };
  }, [stats]);

  const dsTipoAccion = useMemo(() => {
    if (!stats) return null;
    return {
      labels: stats.porTipoAccion.map((t) => TIPO_LABELS[t.tipo] || t.tipo),
      datasets: [{
        data: stats.porTipoAccion.map((t) => t.count),
        backgroundColor: stats.porTipoAccion.map((_, i) => COLORS[i % COLORS.length]),
      }],
    };
  }, [stats]);

  const dsResultado = useMemo(() => {
    if (!stats) return null;
    return {
      labels: ['Encontrado', 'No encontrado'],
      datasets: [{
        data: [stats.porResultado.encontrados, stats.porResultado.noEncontrados],
        backgroundColor: [COLOR_ENCONTRADO, COLOR_NO_ENCONTRADO],
      }],
    };
  }, [stats]);

  const dsTopUsuarios = useMemo(() => {
    if (!stats) return null;
    return {
      labels: stats.topUsuarios.map((u) => u.email),
      datasets: [{
        label: 'Consultas',
        data: stats.topUsuarios.map((u) => u.count),
        backgroundColor: COLORS[5],
      }],
    };
  }, [stats]);

  const dsTopCasinos = useMemo(() => {
    if (!stats) return null;
    return {
      labels: stats.topCasinos.map((c) => c.nombre),
      datasets: [
        {
          label: 'Consultas',
          data: stats.topCasinos.map((c) => c.count),
          backgroundColor: COLORS[0],
          yAxisID: 'y',
        },
        {
          label: 'Hit rate (%)',
          data: stats.topCasinos.map((c) => +(c.hitRate * 100).toFixed(1)),
          backgroundColor: COLORS[3],
          yAxisID: 'y1',
        },
      ],
    };
  }, [stats]);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="p-3">
      <Toast ref={toast} />

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Estadísticas de Consultas</h2>
        {stats?.truncado && (
          <small style={{ color: '#e65100' }}>
            ⚠️ Dataset truncado a 50.000 registros — afiná los filtros para resultados precisos
          </small>
        )}
      </div>

      {/* ── Filtros ────────────────────────────────────────────────────── */}
      <div style={{ background: '#f5f7fa', padding: 16, borderRadius: 8, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <Campo label="Desde">
            <Calendar value={fechaDesde} onChange={(e) => setFechaDesde(e.value)} dateFormat="dd/mm/yy" style={{ width: '100%' }} />
          </Campo>
          <Campo label="Hasta">
            <Calendar value={fechaHasta} onChange={(e) => setFechaHasta(e.value)} dateFormat="dd/mm/yy" style={{ width: '100%' }} />
          </Campo>
          <Campo label="Casino">
            <Dropdown value={casinoId} options={casinos} optionLabel="label" optionValue="value" onChange={(e) => setCasinoId(e.value)} filter style={{ width: '100%' }} />
          </Campo>
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {PRESETS_FECHA.map((p) => (
              <Button key={p.value} label={p.label} size="small" text onClick={() => aplicarPreset(p.value)} />
            ))}
          </div>
          <Button label="Aplicar" icon="pi pi-refresh" onClick={cargarStats} loading={cargando} />
        </div>
      </div>

      {!stats && !cargando && <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Sin datos</div>}

      {stats && (
        <>
          {/* ── KPI Cards ──────────────────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
            <Kpi label="Total consultas" value={fmtNum(stats.totales.consultas)} icon="pi-chart-bar" color="#1976d2" />
            <Kpi label="Casinos activos" value={fmtNum(stats.totales.casinosActivos)} icon="pi-building" color="#43a047" />
            <Kpi label="Usuarios activos" value={fmtNum(stats.totales.usuariosActivos)} icon="pi-users" color="#fb8c00" />
            <Kpi label="Tasa de hit" value={fmtPct(stats.totales.hitRate)} sub={`${fmtNum(stats.totales.encontrados)} de ${fmtNum(stats.totales.consultas)}`} icon="pi-check-circle" color="#e53935" />
            <Kpi
              label="Sin casino asignado"
              value={fmtPct(stats.totales.sinCasinoRate)}
              sub={`${fmtNum(stats.totales.sinCasino)} consultas`}
              icon="pi-exclamation-triangle"
              color={stats.totales.sinCasinoRate > 0 ? '#e65100' : '#888'}
              alert={stats.totales.sinCasinoRate > 0.05}
            />
          </div>

          {/* ── Gráficos: grid 2 cols ─────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            <ChartCard titulo="🍩 Consultas por casino">
              {dsCasinos && <Doughnut data={dsCasinos} options={opcionesDonutFn(esMobile)} />}
            </ChartCard>

            <ChartCard titulo="📈 Tendencia diaria">
              {dsFecha && <Line data={dsFecha} options={opcionesLine} />}
            </ChartCard>

            <ChartCard titulo="🕒 Distribución horaria (0-23h)">
              {dsHora && <Bar data={dsHora} options={opcionesBar} />}
            </ChartCard>

            <ChartCard titulo="📅 Por día de semana">
              {dsDiaSemana && <Bar data={dsDiaSemana} options={opcionesBar} />}
            </ChartCard>

            <ChartCard titulo="🔎 Tipo de acción">
              {dsTipoAccion && <Doughnut data={dsTipoAccion} options={opcionesDonutFn(esMobile)} />}
            </ChartCard>

            <ChartCard titulo="✅ Resultado de la búsqueda">
              {dsResultado && <Doughnut data={dsResultado} options={opcionesDonutFn(esMobile)} />}
            </ChartCard>

            <ChartCard titulo="👤 Top 10 usuarios" alto>
              {dsTopUsuarios && <Bar data={dsTopUsuarios} options={opcionesBarHorizontal} />}
            </ChartCard>

            <ChartCard titulo="🏢 Top 10 casinos (volumen + hit rate)" alto>
              {dsTopCasinos && <Bar data={dsTopCasinos} options={opcionesBarMixto} />}
            </ChartCard>
          </div>

          {/* ── Casinos inactivos (red flag) ──────────────────────────── */}
          <div style={{ marginTop: 20 }}>
            <h3 style={{ margin: '0 0 8px 0', color: '#c62828' }}>🚨 Casinos sin actividad en el período</h3>
            {stats.casinosInactivos.length === 0 ? (
              <div style={{ padding: 12, background: '#e8f5e9', borderRadius: 6, color: '#1b5e20' }}>
                ✓ Todos los casinos activos tuvieron al menos una consulta en este período.
              </div>
            ) : (
              <DataTable value={stats.casinosInactivos} size="small" stripedRows emptyMessage="—">
                <Column field="nombre" header="Casino" sortable />
              </DataTable>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Subcomponentes ──────────────────────────────────────────────────────────

function Campo({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 12, color: '#666', fontWeight: 500 }}>{label}</label>
      {children}
    </div>
  );
}

function Kpi({ label, value, sub, icon, color, alert }) {
  return (
    <div style={{
      background: '#fff',
      border: `1px solid ${alert ? '#e65100' : '#e0e0e0'}`,
      borderLeft: `4px solid ${color}`,
      borderRadius: 8,
      padding: 14,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      <div style={{
        background: color + '22', color, width: 40, height: 40, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <i className={`pi ${icon}`} style={{ fontSize: 18 }} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#222', lineHeight: 1.1 }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: '#888' }}>{sub}</div>}
      </div>
    </div>
  );
}

function ChartCard({ titulo, children, alto }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16,
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      <h4 style={{ margin: '0 0 12px 0', fontSize: 14, color: '#444' }}>{titulo}</h4>
      <div style={{ height: alto ? 320 : 260, position: 'relative' }}>
        {children}
      </div>
    </div>
  );
}

// ─── Opciones de gráficos ────────────────────────────────────────────────────

function opcionesDonutFn(esMobile) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: esMobile ? 'top' : 'right',
        labels: { boxWidth: 12, font: { size: 11 } },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
            const pct = total > 0 ? ((ctx.raw / total) * 100).toFixed(1) : 0;
            return `${ctx.label}: ${ctx.raw} (${pct}%)`;
          },
        },
      },
    },
  };
}

const opcionesBar = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: { y: { beginAtZero: true } },
};

const opcionesLine = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: { y: { beginAtZero: true } },
};

const opcionesBarHorizontal = {
  indexAxis: 'y',
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: { x: { beginAtZero: true } },
};

const opcionesBarMixto = {
  indexAxis: 'y',
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: { beginAtZero: true },
    y: { stacked: false },
    y1: { display: false },
  },
  plugins: {
    tooltip: {
      callbacks: {
        label: (ctx) => ctx.dataset.label === 'Hit rate (%)'
          ? `Hit rate: ${ctx.raw}%`
          : `Consultas: ${ctx.raw}`,
      },
    },
  },
};
